import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { Company } from '../models';
import { environment } from '../../../environments/environment';
import CustomEncoder from '../helpers/custom-encoder';

@Injectable()
export class CompaniesService {
  url: string = environment.url;
  urlGateway = environment.api_gateway;

  constructor(
    private http: HttpClient
  ) {
  }

  getMy(params = {}): Observable<Company> {
    return this.http.get<Company>(`${this.url}/companies/my`, { params: params });
  }

  updateMy(params): Observable<Company> {
    return this.http.patch<Company>(`${this.url}/companies/my`, params);
  }

  getCompanies(params = {}): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.url}/companies`, { params: params });
  }

  getCulturalTestQuestions(params = {}): Observable<any> {
    return this.http.get<any>(`${this.url}/companies/cultural-test`, { params: params });
  }

  createCompany(params): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies`, params);
  }

  // LandingPage.
  postCompanyLandingPagePreview(params = {}): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies/my/landingPage/preview`, params);
  }

  patchCompanyLandingPage(params = {}): Observable<Company> {
    return this.http.patch<Company>(`${this.url}/companies/my/landingPage`, params);
  }

  patchLinkedInId(linkedInId: string): Observable<any> {
    return this.http.patch<any>(`${this.url}/companies/linkedIn`, { linkedInId });
  }

  postCompanyBlacklist(params): Observable<any> {
    return this.http.post<any>(`${this.url}/companies/my/blacklist`, params);
  }

  sendCroppedImageLandingPage(params = {}): Observable<any> {
    return this.http.post<any>(`${this.url}/companies/my/landingPage/photoGallery`, params);
  }

  requestIntegration(companyId: string): Observable<any> {
    return this.http.post<any>(`${this.url}/companies/request-integration/${companyId}`, {});
  }

  getGupyJobs(data = {}): Observable<any> {
    let httpParams = new HttpParams({ encoder: new CustomEncoder() });

    for (const key of Object.keys(data)) {
      httpParams = httpParams.append(key, data[key] instanceof Object ? JSON.stringify(data[key]) : data[key]);
    }

    return this.http.get<Company[]>(`${this.url}/companies/gupy-jobs`, { params: httpParams });
  }

  getCompanyIntegrationsConfig(companyId: string): Observable<any> {
    return this.http.get<any>(`${this.url}/companies/${companyId}/integrations/config`);
  }

  // Showcase
  public getShowcaseCompanies(params = {}): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.url}/companies/showcase`, { params: params });
  };

  public getShowcaseCompanyById(companyId: String, params = {}): Observable<Company> {
    return this.http.get<Company>(`${this.url}/companies/showcase/${companyId}`, { params: params });
  }

  public patchShowcaseCompanyById(companyId: String, params = {}): Observable<any> {
    return this.http.patch<any>(`${this.url}/companies/showcase/${companyId}/resourceBase`, params);
  }

  public patchPartnerShowcaseResource(partnerId: String, params = {}): Observable<Company> {
    return this.http.post<any>(`${this.urlGateway}/api/v1/private/company/partner/${partnerId}/resource`, params);
  }

  public getShowcaseDetailsById(partnerCompanyId: String, params = {}): Observable<Company> {
    return this.http.get<Company>(`${this.urlGateway}/api/v1/private/company/showcase/${partnerCompanyId}`, { params: params });
  }
  // Taqe-Lite

  getCompaniesTalentBase(params = {}): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.url}/companies/talent-base`, { params: params });
  }

  patchVitrinePage(params = {}): Observable<Company> {
    return this.http.patch<Company>(`${this.url}/companies/my/vitrinePage`, params);
  }

  // LandingPage.
  setResourceBase(params = {}): Observable<any> {
    return this.http.post<any>(`${this.urlGateway}/api/company/setresources/v1/`, params);
  }

  addVacancie(params = {}): Observable<any> {
    return this.http.post<any>(`${this.urlGateway}/api/company/add/vacancies/v1/`, params);
  }

  setInvitation(params): Observable<any> {
    return this.http.post<any>(`${this.urlGateway}/api/company/set/invitations/v1/`, params);
  }

  getMe(params = {}): Observable<any> {
    return this.http.get<any>(`${this.urlGateway}/api/company/me/v1/`, params);
  }

  getDiscount(document): Observable<any> {
    return this.http.get<any>(`${this.urlGateway}/api/company/document/partners/check/v1/?document=${document}`, {});
  }

  checkDocument(params = {}): Observable<any> {
    return this.http.post<any>(`${this.urlGateway}/api/company/check/document/v1/`, params);
  }

  updateDocumentPartner(params): Observable<any> {
    return this.http.put<any>(`${this.urlGateway}/api/company/document/partners/v1/${params._id}`, params);
  }

  updateCompany(params): Observable<any> {
    return this.http.put<any>(`${this.urlGateway}/api/company/v1/${params._id}`, params);
  }

  archiveCompany(companyId): Observable<any> {
    return this.http.delete<any>(`${this.urlGateway}/api/company/v1/${companyId}`, {});
  }

  deleteCompany(companyId): Observable<any> {
    return this.http.delete<any>(`${this.urlGateway}/api/company/delete/v1/${companyId}`, {});
  }

  filter(params = {}): Observable<any> {
    return this.http.post<any>(`${this.urlGateway}/api/company/filter/v1/`, params);
  }

  getShowcases(params = {}): Observable<any> {
    return this.http.get<Company[]>(`${this.url}/companies/showcases`, { params: params });
  }

  getCompanyWhatsappNotificationsQuantity(params = {}): Observable<Company> {
    return this.http.get<Company>(`${this.url}/companies/amount-whatsapp-notifications-available`, { params });
  }

  getExportCompaniesList(): Observable<Blob> {
    return this.http.get<Blob>(`${this.urlGateway}/api/v1/private/report/companies`, { responseType: 'Blob' as 'json' });
  }

  postExportCandidates(params = {}): Observable<any> {
    return this.http.post<any[]>(`${this.urlGateway}/api/v1/private/report/companies/candidates`, params);
  }

  postExportCandidatesSuperAdmin(params = {}): Observable<any> {
    return this.http.post<any[]>(`${this.urlGateway}/api/v1/private/report/companies/candidates-super-admin`, params);
  }

  getCompaniesCertifications(params = {}): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}/companies/certifications`, { params: params });
  }

  public getExternalJobs(companyId: string, params = { }): Observable<any[]> {
    let httpParams = new HttpParams({ encoder: new CustomEncoder() });

    for (const key of Object.keys(params)) {
      httpParams = httpParams.append(key, params[key] instanceof Object ? JSON.stringify(params[key]) : params[key]);
    }

    return this.http.get<any[]>(`${this.url}/companies/${companyId}/external/jobs`, { params: httpParams });
  }

  // Resources
  public getAccessToResources(params = {}): Observable<Company> {
    return this.http.get<Company>(`${this.url}/companies/my/resources`, { params: params });
  }

  public changeAccessToCompanyResources(params = {}): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies/resources/change-access`, params);
  }

  public putCompanyAllowTestModule(params = {}): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies/resources/allow-module-test`, params);
  }

  public putCompanyAllowCreateShowcase(params = {}): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies/resources/allow-create-showcase`, params);
  }

  public postCompanyIsAppV4(params = {}): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies/resources/app-v4-settings`, params);
  }

  public putCompanyWhatsappNotifications(params = {}): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies/resources/allow-whatsapp-notifications`, params);
  }

  public putCompanyAllowCreateSimplifiedApplication(params = {}): Observable<Company> {
    return this.http.post<Company>(`${this.url}/companies/resources/allow-simplified-application`, params);
  }

  public getTotvsCourses(params = {}): Observable<any> {
    return this.http.get<any>(`${this.url}/companies/rede-cidada/totvs/courses`, params);
  }
}
